import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import de from './locales/de.json';
import en from './locales/en.json';
// HOF-696 D19: scoped to what a worker sees (utils/language.ts
// WORKER_SCOPED_NAMESPACES); every other key falls back to German.
import ro from './locales/ro.json';
import pl from './locales/pl.json';
import { getCookie, setCookie } from './utils/cookies';
import {
  LANGUAGE_COOKIE,
  LANGUAGE_COOKIE_DAYS,
  LEGACY_LANGUAGE_STORAGE_KEY,
  isSupportedLanguage,
  resolveInitialLanguage,
} from './utils/language';

// This module resolves the language ONCE, at import time, before React exists
// (`src/index.tsx` imports it first). The guard keeps that safe in a DOM-less
// runtime: the prerender entry builds its own i18next instance and never
// imports this file, but the guard is one line and removes a whole class of
// future SSR breakage.
const hasDom = typeof document !== 'undefined' && typeof window !== 'undefined';

// #3132 — resolution ladder: `hofsync_lang` cookie → legacy `i18nextLng` →
// browser language, ro/pl only (HOF-696) → domain default (.com ⇒ en,
// everything else ⇒ de) → de.
const initialLanguage = hasDom
  ? resolveInitialLanguage({
      cookie: getCookie(LANGUAGE_COOKIE),
      legacyLocalStorage: localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY),
      hostname: window.location.hostname,
      navigatorLanguages:
        typeof navigator !== 'undefined'
          ? (navigator.languages?.length ? navigator.languages : [navigator.language])
          : [],
    })
  : 'de';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
      en: { translation: en },
      ro: { translation: ro },
      pl: { translation: pl }
    },
    lng: initialLanguage, // Resolved above; see the ladder in src/utils/language.ts
    fallbackLng: 'de',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false // React already escapes
    },
    // NOTE: the `order` half of this block has never run. i18next only invokes
    // the LanguageDetector when `lng` is undefined, and `lng` above is always
    // supplied — so the detector's `navigator` fallback has never fired in
    // production. Resolution lives in `resolveInitialLanguage`, not here; since
    // HOF-696 that ladder has its own navigator rung (ro/pl only).
    //
    // The `caches` half IS live: the detector writes `i18nextLng` back to
    // localStorage on every `languageChanged`. Keep it. It is what
    // `e2e/tests/i18n.spec.ts` pins, and it is the legacy rung the resolver
    // reads for users who chose a language before #3132. Dual-write, with the
    // cookie winning on read.
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

// #3132 — persist the choice for 12 months.
//
// Registered on the singleton rather than in `Footer.tsx` (the only switcher
// today) so that ANY switcher gets persistence for free, and — more
// importantly — so `Footer.tsx` stays unmodified. The prerender pulls
// `Footer.tsx` into a Node module graph, and a browser-only import reached
// from there would break the static build.
//
// The prerender's own instance is a separate `createInstance()`, so it never
// receives this listener.
if (hasDom) {
  // The page's language follows the chosen one (HOF-696 ui e2e): with <html lang="de"> on a Romanian
  // screen, Android Chrome offers to "translate from German" and mangles the worker app.
  const setPageLanguage = (lng: string) => {
    document.documentElement.lang = isSupportedLanguage(lng) ? lng : 'de';
  };
  setPageLanguage(i18n.language);
  i18n.on('languageChanged', setPageLanguage);
  i18n.on('languageChanged', (lng) => {
    // i18next fires this for languages we have no bundle for (it then falls
    // back to `de` for lookups). Persisting one would put a value in the
    // cookie that the resolver has to reject on every subsequent boot.
    if (isSupportedLanguage(lng)) {
      setCookie(LANGUAGE_COOKIE, lng, { days: LANGUAGE_COOKIE_DAYS });
    }
  });
}

export default i18n;
